import { formatName } from "helpers/miscHelpers";

import countryCodes from "../../static/allCountryCodes.json";
import stateCodes from "../../static/stateCodes.json";
import popData from "../../static/pop_data.json";
import statePopData from "../../static/us_pop_data.json";

export const stateNames = stateCodes.map(s => s.State.toLowerCase());
// let usOnlyData;

const totalWorldPop = 7794798739;
const totalUSPop = 330468830;

/* ------ helper functions ------- */
export function stateCodeToName(code) {
  const found = stateCodes.find(x => x.Code === code);

  if (found) {
    return found.State;
  }

  return false;
}

export function countrySorter(a, b) {
  if (a.country < b.country) {
    return -1;
  }
  if (a.country > b.country) {
    return 1;
  }
  return 0;
}

export function stateSorter(a, b) {
  if (a.state < b.state) {
    return -1;
  }
  if (a.state > b.state) {
    return 1;
  }
  return 0;
}

export function stateSorterJH(a, b) {
  if (a.Province_State < b.Province_State) {
    return -1;
  }
  if (a.Province_State > b.Province_State) {
    return 1;
  }
  return 0;
}

// Arithmetic mean
const getMean = function(data) {
  return (
    data.reduce(function(a, b) {
      return Number(a) + Number(b);
    }) / data.length
  );
};

// Standard deviation
const getSD = function(data) {
  const m = getMean(data);
  return Math.sqrt(
    data.reduce(function(sq, n) {
      return sq + Math.pow(n - m, 2);
    }, 0) / data.length
  );
};

function totalsReducer(data, cols, type) {
  // console.log("totalsReducer -> cols", cols);
  // console.log("totalsReducer -> type", type);
  // console.log("totalsReducer -> data", data);

  const output = cols.reduce((finalObj, curCol) => {
    const total = data.reduce((acc, curr) => {
      if (typeof curr.timeline[`${type}`][`${curCol}`] === "number") {
        return acc + Number(curr.timeline[`${type}`][`${curCol}`]);
      }

      return acc;
    }, 0);

    return {
      ...finalObj,
      [`${curCol}`]: total,
    };
  }, {});

  return output;
}

function totalPopReducer(data, cols, isUS = false) {
  const output = cols.reduce((finalObj, curCol) => {
    const total = data.reduce((acc, curr) => {
      if (typeof curr.timeline.cases[`${curCol}`] === "number") {
        return acc + Number(curr.timeline.cases[`${curCol}`]);
      }
      return acc + 0;
    }, 0);

    if (isUS) {
      return {
        ...finalObj,
        [`${curCol}`]: total / (totalUSPop / 1000),
      };
    }

    return {
      ...finalObj,
      [`${curCol}`]: total / (totalWorldPop / 1000),
    };
  }, {});

  return output;
}

function totalPopDeathsReducer(data, cols, isUS = false) {
  const output = cols.reduce((finalObj, curCol) => {
    const total = data.reduce((acc, curr) => {
      if (typeof curr.timeline.deaths[`${curCol}`] === "number") {
        return acc + Number(curr.timeline.deaths[`${curCol}`]);
      }
      return acc + 0;
    }, 0);

    if (isUS) {
      return {
        ...finalObj,
        [`${curCol}`]: total / (totalUSPop / 1000),
      };
    }

    return {
      ...finalObj,
      [`${curCol}`]: total / (totalWorldPop / 1000),
    };
  }, {});

  return output;
}

function totalPopCasesReducer(data, cols, isUS = false) {
  const output = cols.reduce((finalObj, curCol) => {
    const total = data.reduce((acc, curr) => {
      if (typeof curr.timeline.cases[`${curCol}`] === "number") {
        return acc + Number(curr.timeline.cases[`${curCol}`]);
      }
      return acc + 0;
    }, 0);

    if (isUS) {
      return {
        ...finalObj,
        [`${curCol}`]: total / (totalUSPop / 1000),
      };
    }

    return {
      ...finalObj,
      [`${curCol}`]: total / (totalWorldPop / 1000),
    };
  }, {});

  return output;
}

export function createTotalsObject(data) {
  const cols = Object.keys(data[0].timeline.cases);
  const totalCasesObj = totalsReducer(data, cols, "cases");
  const totalDeathsObj = totalsReducer(data, cols, "deaths");
  const totalNewCasesObj = totalsReducer(data, cols, "newCases");
  const totalNewDeathsObj = totalsReducer(data, cols, "newDeaths");
  const totalDeathsPer100kObj = totalPopDeathsReducer(data, cols);
  const totalCasesPer100kObj = totalPopCasesReducer(data, cols);

  return {
    country: "World Total",
    province: null,
    timeline: {
      cases: totalCasesObj,
      deaths: totalDeathsObj,
      newCases: totalNewCasesObj,
      newDeaths: totalNewDeathsObj,
      casesPer100k: totalCasesPer100kObj,
      deathsPer100k: totalDeathsPer100kObj,
    },
  };
}

export function createTotalsObjectStates(data) {
  const cols = Object.keys(data[0].timeline.cases);
  const totalCasesObj = totalsReducer(data, cols, "cases");
  const totalDeathsObj = totalsReducer(data, cols, "deaths");
  const totalNewCasesObj = totalsReducer(data, cols, "newCases");
  const totalNewDeathsObj = totalsReducer(data, cols, "newDeaths");
  const totalCasesPer100kObj = totalPopCasesReducer(data, cols, true);
  const totalDeathsPer100kObj = totalPopDeathsReducer(data, cols, true);

  return {
    name: "us total",
    timeline: {
      cases: totalCasesObj,
      deaths: totalDeathsObj,
      newCases: totalNewCasesObj,
      newDeaths: totalNewDeathsObj,
      casesPer100k: totalCasesPer100kObj,
      deathsPer100k: totalDeathsPer100kObj,
    },
  };
}

function getSectionData(data, type, dateFields) {
  const blankArray = Array(dateFields.length).fill(0);
  const casesTotal = data.reduce((tot, cur) => {
    const totals = dateFields.map(df => cur.timeline[`${type}`][`${df}`]);
    return tot.map((t, i) => parseInt(t) + parseInt(totals[i]));
  }, blankArray);

  const totalCasesEntries = casesTotal.map((t, ind) => [
    dateFields[ind],
    Number(t),
  ]);
  const totalCasesObj = Object.fromEntries(totalCasesEntries);

  return totalCasesObj;
}

// function getSectionDataStates(data, type, dateFields) {
//   const totals = dateFields.map(df => data.timeline[`${type}`][`${df}`]);
//   const totalCasesEntries = totals.map((t, ind) => [
//     dateFields[ind],
//     Number(t),
//   ]);
//   const totalCasesObj = Object.fromEntries(totalCasesEntries);

//   return totalCasesObj;
// }

function getSplitCountryData(data, isUS = false) {
  const splitCountries = [
    "Australia",
    "China",
    "Canada",
    "Denmark",
    "France",
    "UK",
  ];

  const output = splitCountries
    .map((country, ci) => {
      const founds = data.filter(
        x => x.country.toLowerCase() === country.toLowerCase()
      );

      if (founds) {
        // calculate totals and merge
        const dateFields = Object.keys(founds[0].timeline.cases);

        const totalCasesObj = getSectionData(founds, "cases", dateFields);
        const totalDeathsObj = getSectionData(founds, "deaths", dateFields);

        return {
          country: formatName(country),
          province: "",
          timeline: {
            cases: totalCasesObj,
            deaths: totalDeathsObj,
          },
        };
      }
      return null;
    })
    .filter(x => x);

  return output;
}

function injectDataNewCases(data) {
  data.forEach((place, i) => {
    const { cases } = place.timeline;
    const newCases = {};
    const caseKeys = Object.keys(cases);

    caseKeys.forEach((c, j) => {
      const val = j === 0 ? 0 : cases[`${c}`] - cases[`${caseKeys[j - 1]}`];
      Object.assign(newCases, { [`${c}`]: val });
    });

    place.timeline.newCases = newCases;
  });
}

function injectDataNewDeaths(data) {
  data.forEach((place, i) => {
    const { deaths } = place.timeline;
    const newDeaths = {};
    const theKeys = Object.keys(deaths);

    theKeys.forEach((c, j) => {
      const val = j === 0 ? 0 : deaths[`${c}`] - deaths[`${theKeys[j - 1]}`];
      Object.assign(newDeaths, { [`${c}`]: val });
    });

    place.timeline.newDeaths = newDeaths;
  });
}

// function injectDataActiveCases(data) {
//   data.forEach(x => {
//     const { cases, deaths, recovered } = x.timeline;
//     const actives = {};
//     for (const [key, value] of Object.entries(cases)) {
//       const activeNum =
//         parseInt(value) - (parseInt(deaths[key]) + parseInt(recovered[key]));
//       Object.assign(actives, { [`${key}`]: activeNum });
//     }

//     x.timeline.activeCases = actives;
//   });
// }

// 7 day SMA
export function injectSimpleMovingAvg(data, timeframe = 15) {
  const locationKeys = Object.keys(data[0]).filter(x => x !== "name");

  data.forEach((d, i) => {
    let lastFewItems;

    if (i > 0 && i < timeframe) {
      lastFewItems = data.slice(0, i + 1);
    } else {
      lastFewItems = data.slice(i - (timeframe - 1), i + 1);
    }

    locationKeys.forEach((loc, z) => {
      const lastFewTotals = lastFewItems.reduce(
        (acc, curr) => acc + curr[`${loc}`],
        0
      );
      d[`sma_${loc}`] = lastFewTotals / lastFewItems.length;
    });
  });
}

export function injectBBands(data, timeframe = 15) {
  const locationKeys = Object.keys(data[0]).filter(
    x => x !== "name" && !x.includes("_")
  );

  data.forEach((d, i) => {
    let lastFewItems;

    if (i > 0 && i < timeframe) {
      // return;
      lastFewItems = data.slice(0, i + 1);
    } else {
      lastFewItems = data.slice(i - (timeframe - 1), i + 1);
    }

    locationKeys.forEach((loc, z) => {
      const lastFewValues = lastFewItems.map(item => item[`${loc}`]);
      const stdDev = lastFewValues.length > 0 ? getSD(lastFewValues) : 0;
      // d[`bbUpper_${loc}`] = d[`sma_${loc}`] + stdDev * 2;
      // d[`bbLower_${loc}`] = d[`sma_${loc}`] - stdDev * 2;
      d[`bbands_${loc}`] = [
        d[`sma_${loc}`] + stdDev * 2,
        d[`sma_${loc}`] - stdDev * 2,
      ];
    });
  });
}

function injectDataDeathsPer100k(data) {
  data.forEach(x => {
    const { deaths } = x.timeline;
    const deathsPer100k = {};
    const countryName = x.country;

    for (const [key, value] of Object.entries(deaths)) {
      const activeNum = parseInt(value);
      const popEntry = popData.data.find(
        p => p.name.toLowerCase() === countryName.toLowerCase()
      );

      if (popEntry) {
        const per100k = Number(
          (activeNum / (popEntry.pop2020 * 1000)) * 100000
        ).toFixed(9);

        Object.assign(deathsPer100k, {
          [`${key}`]: Number(per100k),
        });
      } else {
        // do nothing for now
      }
    }

    x.timeline.deathsPer100k = deathsPer100k;
  });
}

function injectDataDeathsPer100kStates(data) {
  data.forEach(x => {
    const { deaths } = x.timeline;
    const deathsPer100k = {};
    const stateName = x.name;

    for (const [key, value] of Object.entries(deaths)) {
      const activeNum = parseInt(value);
      const popEntry = statePopData.data.find(
        p => p.State.toLowerCase() === stateName.toLowerCase()
      );

      if (popEntry) {
        const per100k = Number((activeNum / popEntry.Pop) * 100000).toFixed(9);

        Object.assign(deathsPer100k, {
          [`${key}`]: Number(per100k),
        });
      } else {
        // do nothing for now
      }
    }

    x.timeline.deathsPer100k = deathsPer100k;
  });
}

function injectDataCasesPer100k(data) {
  data.forEach(x => {
    const { cases } = x.timeline;
    const casesPer100k = {};
    const countryName = x.country;

    for (const [key, value] of Object.entries(cases)) {
      const activeNum = parseInt(cases[key]);
      const popEntry = popData.data.find(
        p => p.name.toLowerCase() === countryName.toLowerCase()
      );

      if (popEntry) {
        const per100k = Number(
          (activeNum / (popEntry.pop2020 * 1000)) * 100000
        ).toFixed(9);
        Object.assign(casesPer100k, {
          [`${key}`]: Number(per100k),
        });
      } else {
        // do nothing for now
      }
    }

    x.timeline.casesPer100k = casesPer100k;
  });
}

function injectDataCasesPer100kStates(data) {
  data.forEach(x => {
    const { cases } = x.timeline;
    const casesPer100k = {};
    const stateName = x.name;

    for (const [key, value] of Object.entries(cases)) {
      const activeNum = parseInt(cases[key]);
      const popEntry = statePopData.data.find(
        p => p.State.toLowerCase() === stateName.toLowerCase()
      );

      if (popEntry) {
        const per100k = Number((activeNum / popEntry.Pop) * 100000).toFixed(9);
        Object.assign(casesPer100k, {
          [`${key}`]: Number(per100k),
        });
      } else {
        // do nothing for now
      }
    }

    x.timeline.casesPer100k = casesPer100k;
  });
}

/* ------ end helper functions ------- */

export function processDataNew(fullData) {
  // get non-split countries
  const filteredData = countryCodes
    .map((country, ci) => {
      const found = fullData.find(x => {
        let theCountry = x.country;

        if (theCountry.toLowerCase() === "s. korea") {
          theCountry = "South Korea";
        }

        if (theCountry.toLowerCase() === "usa") {
          theCountry = "United States";
        }

        return theCountry.toLowerCase() === country.Name.toLowerCase();
      });

      if (found && !found.province) {
        // convert strings to numbers
        const cases = Object.fromEntries(
          Object.entries(found.timeline.cases).map(e => [e[0], Number(e[1])])
        );

        const deaths = Object.fromEntries(
          Object.entries(found.timeline.deaths).map(e => [e[0], Number(e[1])])
        );

        return {
          country: formatName(found.country),
          province: null,
          timeline: {
            cases,
            deaths,
          },
        };
      }
    })
    .filter(x => x);

  // get split countries
  const splitTotals = getSplitCountryData(fullData);

  filteredData.push(...splitTotals);

  injectDataNewCases(filteredData);
  injectDataNewDeaths(filteredData);
  injectDataCasesPer100k(filteredData);
  injectDataDeathsPer100k(filteredData);

  return { data: filteredData.sort(countrySorter) };
}

export function processStateDataNew(fullData) {
  injectDataNewCases(fullData);
  injectDataNewDeaths(fullData);
  injectDataCasesPer100kStates(fullData);
  injectDataDeathsPer100kStates(fullData);

  return { data: fullData.sort(stateSorter) };
}

function convertAmazonDate(date) {
  const dateChars = date.toString().split("");
  const year = dateChars.slice(2, 4).join("");
  const month = dateChars.slice(4, 6).join("");
  const day = dateChars.slice(6, 8).join("");

  return `${month}/${day}/${year}`;
}

export function processAmazonData(fullData) {
  const stateObjects = stateCodes.map(st => {
    const stateAll = fullData.filter(f => f.state === st.Code);
    const stateObj = {
      name: st.State,
      timeline: {},
    };

    const keysToSkip = [
      "date",
      "dateChecked",
      "fips",
      "hash",
      "state",
      "total",
    ];
    const keysToUse = Object.keys(stateAll[0]).filter(
      z => !keysToSkip.includes(z)
    );

    keysToUse.forEach(k => {
      stateObj.timeline[`${k}`] = {};
    });
    // console.log("processAmazonData -> keysToUse", keysToUse);

    stateAll.forEach(state => {
      const theDate = convertAmazonDate(state.date);

      keysToUse.forEach(key => {
        const theValue = state[`${key}`] ? state[`${key}`] : 0;
        const tempObj = { [`${theDate}`]: theValue };
        Object.assign(stateObj.timeline[`${key}`], tempObj);
      });
    });

    // need to reverse timeline direction here
    keysToUse.forEach(k => {
      const reversedEntries = Object.entries(
        stateObj.timeline[`${k}`]
      ).reverse();
      stateObj.timeline[`${k}`] = Object.fromEntries(reversedEntries);
    });

    return stateObj;
  });
  return stateObjects;
}

export function createTotalsObjectAmazon(data) {
  console.log("test");
}
